import { useParams } from 'react-router-dom';
import { useQuery } from 'react-query';
import styled from 'styled-components';
import { BackLink, Button, Loader, WidgetLayout } from 'src/components';
import { DiscountChip } from 'src/components/DiscountChip';
import BenefitsService from 'src/services/BenefitsService';
import { BenefitId } from 'src/models/Benefit/Benefit';
import { useScrollToTop } from 'src/hooks';
import sanitizeHtml from 'src/utils/sanitizeHtml';
import OtherBenefits from './OtherBenefits';
import { resolveCtaLink } from './ctaLink';

const LoaderArea = styled.div`
  height: 100%;
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const Logo = styled(WidgetLayout.ContentWithMargin)`
  margin-bottom: 24px;

  img {
    display: block;
    max-width: 160px;
    width: 100%;
    height: auto;
    object-fit: contain;
  }
`;

const Title = styled.h1`
  margin: 0;
  font-size: 24px;
  line-height: 120%;
  letter-spacing: 0.3px;
  color: ${p => p.theme.palette.text.high};
`;

const ErrorCopy = styled(WidgetLayout.ContentWithMargin)`
  margin-top: 24px;
  font-size: 16px;
  line-height: 150%;
  color: ${p => p.theme.palette.text.medium};
`;

const ChipRow = styled(WidgetLayout.ContentWithMargin)`
  margin-top: 16px;
`;

const Content = styled(WidgetLayout.ContentWithMargin)`
  margin-top: 24px;
  font-size: 16px;
  line-height: 24px;
  letter-spacing: 0.15px;
  color: ${p => p.theme.palette.text.high};

  p {
    margin: 0 0 16px;
  }

  img {
    max-width: 100%;
    height: auto;
  }
`;

const CtaRow = styled(WidgetLayout.ContentWithMargin)`
  margin-top: 32px;
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
`;

const DetailImage = styled(WidgetLayout.ContentWithMargin)`
  margin-top: 32px;

  img {
    display: block;
    width: 100%;
    height: auto;
    border-radius: 8px;
  }
`;

const BenefitDetail: React.FC = () => {
  const { id } = useParams<{ id: BenefitId }>();
  useScrollToTop([id]);

  const { data: benefit, isLoading } = useQuery(
    ['BenefitDetail', id],
    () => BenefitsService.getOne(id),
    { enabled: !!id },
  );

  if (!id) {
    return <Loader />;
  }

  const ctas = (benefit?.ctas ?? []).flatMap((cta) => {
    const target = resolveCtaLink(cta.link);
    return cta.label && target ? [{ label: cta.label, target }] : [];
  });

  return (
    <WidgetLayout>
      <WidgetLayout.MainContentArea>
        {isLoading && (
          <LoaderArea>
            <Loader />
          </LoaderArea>
        )}
        {!isLoading && !benefit && (
          <>
            <BackLink to="/benefits" label="Twoje benefity" />
            <ErrorCopy>
              Nie znaleźliśmy tego benefitu albo nie udało się go załadować.
              Wróć do listy i spróbuj ponownie.
            </ErrorCopy>
          </>
        )}
        {!isLoading && benefit && (
          <>
            <BackLink to="/benefits" label="Twoje benefity" />
            {benefit.logo && (
              <Logo>
                <img src={benefit.logo} alt="" />
              </Logo>
            )}
            <Title>{benefit.name}</Title>
            {benefit.discount && (
              <ChipRow>
                <DiscountChip label={benefit.discount} />
              </ChipRow>
            )}
            {benefit.content && (
              <Content
                dangerouslySetInnerHTML={{ __html: sanitizeHtml(benefit.content) }}
              />
            )}
            {ctas.length > 0 && (
              <CtaRow>
                {ctas.map(({ label, target }, index) => (
                  <Button
                    key={`${label}-${target.kind === 'internal' ? target.routerLink : target.href}-${index}`}
                    label={label}
                    {...(target.kind === 'internal'
                      ? { routerLink: target.routerLink }
                      : { href: target.href })}
                    color="primary"
                    variant="filled"
                    rounded
                    bold
                    openInNewTab={target.kind === 'external'}
                  />
                ))}
              </CtaRow>
            )}
            {benefit.detailImage && (
              <DetailImage>
                <img src={benefit.detailImage} alt="" />
              </DetailImage>
            )}
          </>
        )}
      </WidgetLayout.MainContentArea>
      {(!isLoading && benefit) && (
        <WidgetLayout.WidgetArea>
          <OtherBenefits benefitId={id} />
        </WidgetLayout.WidgetArea>
      )}
    </WidgetLayout>
  );
};

export default BenefitDetail;
